import CookieStorage from './cookieStorage';
import MemoryStorage from './memoryStorage';
import { IDENTITY_STORAGE_KEY } from '../sdk/constants';
/**
 * Initializes storage based on the circumstances. You can either select session storage or longer term storage
 * If localStorage and sessionStorage are not available due to quota or browser restrictions, a js cookie will be used.
 * The storage returned will follow the {Storage} interface which is what localStorage uses; however, certain functions may
 * not be available with CookieStorage (e.g. selecting by index, clear(), length are not supported).
 *
 * @hidden
 * @param {boolean} [isSession] if true, the selected storage will be session based (e.g. sessionStorage). If false, longer term storage will be used. (e.g. localStorage)
 * @returns
 * The selected storage (CookieStorage, sessionStorage or localStorage)
 */
export function initStorage(baseDomain: string = '', isSession?: boolean, expDate?: Date) {
  let session = isSession;
  if (typeof window !== 'undefined') {
    const cookieStorage = new CookieStorage(undefined, undefined, expDate);
    if (baseDomain) {
      const cs = new CookieStorage(baseDomain, isSession, expDate);
      localStorage.removeItem(IDENTITY_STORAGE_KEY);
      sessionStorage.removeItem(IDENTITY_STORAGE_KEY);
      return cs;
    }

    if (typeof session === 'undefined') {
      // then we need to figure out if we have stuff stored in the session or not by inspecting previous storage
      // this is useful for page reloads which would otherwise be "forgetting" that this is a non-rememberme session.

      if (
        !!sessionStorage.getItem(IDENTITY_STORAGE_KEY) &&
        sessionStorage.getItem(IDENTITY_STORAGE_KEY) !== '{}'
      ) {
        session = true;
      } else if (
        !!localStorage.getItem(IDENTITY_STORAGE_KEY) &&
        localStorage.getItem(IDENTITY_STORAGE_KEY) !== '{}'
      ) {
        session = false;
      } else {
        const cs = new CookieStorage(baseDomain, undefined, expDate);
        const data = JSON.parse(cs.getItem(IDENTITY_STORAGE_KEY) || '{}');
        if (data && Object.keys(data).length > 0) {
          session = cs.isSession(IDENTITY_STORAGE_KEY);
        } else {
          session = false; // default to false for backwards compatibility
        }
      }
    }

    try {
      // check if we can write to session storage & localstorage
      const testKey = `ArcId.test`;
      if (session) {
        sessionStorage.setItem(testKey, 'testValue');
        sessionStorage.removeItem(testKey);
        localStorage.removeItem(IDENTITY_STORAGE_KEY);
        cookieStorage.removeItem(IDENTITY_STORAGE_KEY);
        return sessionStorage;
      }

      localStorage.setItem(testKey, 'testValue');
      localStorage.removeItem(testKey);
      sessionStorage.removeItem(IDENTITY_STORAGE_KEY);
      cookieStorage.removeItem(IDENTITY_STORAGE_KEY);

      return localStorage;
    } catch (_) {
      localStorage.removeItem(IDENTITY_STORAGE_KEY);
      sessionStorage.removeItem(IDENTITY_STORAGE_KEY);
      // for old safari in private mode or no space in webstorage.
      return new CookieStorage(baseDomain, session, expDate);
    }
  } else {
    return new MemoryStorage();
  }
}

/**
 * @hidden
 */
export function shouldStorageChange(
  selectedStorage: CookieStorage | Storage | MemoryStorage | undefined,
  isSession: boolean,
  storageKey: string
) {
  return (
    !selectedStorage ||
    (selectedStorage instanceof CookieStorage &&
      selectedStorage.isSession(storageKey) !== isSession) ||
    (selectedStorage === sessionStorage && !isSession) ||
    (selectedStorage === localStorage && isSession)
  );
}
